import * as React from 'react';
import {
  cn,
  dndCardDetails,
  dndMove,
  parseField,
  SchemalessFilterSchema
} from '../../utils';
import { Icon, Trash } from 'rcd-cpp-ui/ui/icon';
import { Add, Ascending, Descending, SixDots } from '../Icon/icon';
import { SchemaFieldTypeahead } from '../SchemaFieldTypeahead/schemaFieldTypeahead';
import './multiSort.scss';
import { SortOrder } from '../../enums/SortOrder';
import { SchemaField, View, Schema } from '../../types';
import { FieldType } from '../../enums/FieldType';
import { useSelector } from '../../utils/useSelector';

interface SortField extends View.SortByField {
  isExpanded: boolean;
}

interface SchemaFieldObj extends Omit<SchemaField, 'order'> {
  isExpanded?: boolean;
  order?: SortOrder;
}

type Props = {
  dataSchema: Schema;
  sortBy: View.SortByField[];
  disabledFields?: string[];
  viewFields: View.Field[];
  setSortBy: (sortBy: View.SortByField[]) => any;
};

export const MultiSort = ({
  dataSchema,
  sortBy,
  viewFields,
  disabledFields,
  setSortBy
}: Props) => {
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  if (!dataSchema) return null;
  const scrollContainer = React.useRef(null);

  const [displaySortBy, setDisplaySortBy] = React.useState<SortField[]>(
    sortBy?.map(sb => {
      return {
        ...sb,
        isExpanded: false,
        order: sb?.order ? sb.order : SortOrder.asc
      };
    }) || []
  );

  const updateSortByLists = (sortByList: SortField[]) => {
    const updatedListWithValues = sortByList.filter(f => !!f?.field);
    setDisplaySortBy(sortByList);
    if (isSortUpdated(sortBy, updatedListWithValues)) {
      const updatedSorts = updatedListWithValues.map(s => ({ ...s }));
      setSortBy(updatedSorts);
    }
  };

  const isSortUpdated = (
    previousSorts: View.SortByField[],
    updatedSorts: View.SortByField[]
  ): boolean => {
    if (previousSorts?.length != updatedSorts?.length) {
      return true;
    }
    let isUpdated = false;
    previousSorts.forEach((item, index) => {
      if (
        updatedSorts[index].field !== item.field ||
        updatedSorts[index].order !== item.order
      ) {
        isUpdated = true;
      }
    });

    return isUpdated;
  };

  const handleUpdateField = (f: SchemaFieldObj, index: number) => {
    if (!!f) {
      const fieldBeingUpdated = displaySortBy[index];
      const updatedField = { ...(fieldBeingUpdated || {}), ...f };
      let updatedSortByList = JSON.parse(
        JSON.stringify(
          (displaySortBy || [])?.map(dsb => {
            return { ...dsb, isExpanded: false };
          })
        )
      );
      updatedSortByList[index] = updatedField;
      updateSortByLists(updatedSortByList);
    } else {
      let updatedSortByList = JSON.parse(
        JSON.stringify(
          (displaySortBy || [])?.map(dsb => {
            return { ...dsb, isExpanded: false };
          })
        )
      );

      updatedSortByList.splice(index, 1);
      updateSortByLists(updatedSortByList);
    }
  };

  const createExcludedFields = (isSeeAll?: boolean) => {
    let excluded = ['Item.companionBy'];
    schemas?.forEach(s => {
      s?.props?.fields?.forEach(f => {
        if (f.excludeFromView) {
          excluded.push(f.key);
        }
        if (f.fieldType == FieldType.custom) excluded.push(f.key);
        if (!isSeeAll && !viewFields.includes(f.key)) excluded.push(f.key);
      });
    });
    displaySortBy.forEach(item => {
      excluded.push(item.field);
    });
    SchemalessFilterSchema.props.fields.forEach(f => excluded.push(f.key));
    return excluded;
  };

  const renderSortBy = () => {
    return (
      <>
        {!!displaySortBy &&
          displaySortBy.map((element, index: number) => {
            let { field, order, isExpanded } = element;
            return (
              <MultiSortCard
                key={`${field}_${index}`}
                disabledFields={disabledFields}
                index={index}
                field={field}
                sortOrder={order == 'asc' ? SortOrder.asc : SortOrder.desc}
                onMove={dndMove(displaySortBy, updateSortByLists)}
                expandField={isExpanded}
                onFieldChange={obj => {
                  handleUpdateField(obj, index);
                }}
                excludeFields={isShowAllFields => {
                  return createExcludedFields(isShowAllFields);
                }}
              />
            );
          })}
      </>
    );
  };
  React.useEffect(() => {
    renderSortBy();
  }, [displaySortBy]);

  return (
    <div className="multi-sort-container">
      <div
        className="add-button"
        onClick={() => {
          const newField: SortField = {
            field: '',
            order: SortOrder.asc,
            isExpanded: true
          };
          const updatedDisplaySortBy = displaySortBy.map(item => ({
            ...item,
            isExpanded: false
          }));
          setDisplaySortBy([...updatedDisplaySortBy, newField]);
        }}
      >
        <Add />
        <div>Add Sort Criteria</div>
      </div>
      <div className="multi-sort-container">
        <div ref={scrollContainer}>{renderSortBy()}</div>
      </div>
    </div>
  );
};

const MultiSortCard = ({
  disabledFields,
  index,
  field,
  onMove,
  sortOrder,
  expandField,
  onFieldChange,
  excludeFields
}: {
  disabledFields: string[];
  index: number;
  field?: string;
  sortOrder: SortOrder;
  onMove?: Function;
  expandField: boolean;
  onFieldChange: Function;
  excludeFields: Function;
}) => {
  const { ref, opacity, handlerId } = dndCardDetails(onMove, field, index);
  const dataSchema = useSelector(s => s.view?.selected?.dataSchema);
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  const [isExpanded, setIsExpanded] = React.useState(expandField);
  React.useEffect(() => {
    setIsExpanded(expandField);
  }, [expandField]);
  const dataType = dataSchema?.props?.type;
  const [order, setOrder] = React.useState(SortOrder.asc);
  const [showAllFields, setShowAllFields] = React.useState(false);
  const { schemaField } = parseField(field, schemas);
  const isDisabled = disabledFields?.includes(field);

  return (
    <div
      className={cn('multi-sort-card-group', { ['mscg-disabled']: isDisabled })}
    >
      {!isExpanded && (
        <div
          className="multi-sort-card"
          ref={isDisabled ? null : ref}
          style={{ opacity }}
          data-handler-id={handlerId}
          onClick={
            isDisabled
              ? null
              : () => {
                  setIsExpanded(!isExpanded);
                  const newField: SchemaFieldObj = {
                    field: field,
                    isExpanded: !isExpanded
                  };
                  onFieldChange(newField);
                }
          }
        >
          <div className="card-text">{schemaField?.displayName || '--'}</div>
          <div className="option-card-order">
            {sortOrder == SortOrder.asc ? <Ascending /> : <Descending />}
            <SixDots />
          </div>
        </div>
      )}
      {isExpanded && (
        <div
          onClick={() => {
            setIsExpanded(!isExpanded);
            const newField: SchemaFieldObj = {
              field: field,
              isExpanded: !isExpanded
            };
            onFieldChange(newField);
          }}
          className="multi-sort-card expanded"
          ref={ref}
          style={{ opacity }}
          data-handler-id={handlerId}
        >
          <div
            className="option-card-column-left"
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <div className="option-card-header">Select Field</div>
            <SchemaFieldTypeahead
              selected={parseField(field, schemas).schemaField}
              flat
              disabled={!dataType}
              onSelectField={f => {
                const newField: SchemaFieldObj = {
                  field: f.key,
                  isExpanded: false
                };
                onFieldChange(newField);
              }}
              excludeFields={excludeFields(showAllFields)}
            >
              <div className="typeahead-popular-fields">
                <div
                  onClick={() => {
                    setShowAllFields(!showAllFields);
                  }}
                >
                  See {showAllFields ? 'Less' : 'All'}
                </div>
              </div>
            </SchemaFieldTypeahead>
          </div>
          <>
            <div className="option-card-column-right">
              <div className="option-card-header">Order</div>
              <div
                onClick={e => {
                  e.stopPropagation();
                  let newSortOrder =
                    order == SortOrder.asc ? SortOrder.desc : SortOrder.asc;
                  setOrder(newSortOrder);
                  const changedField: SchemaFieldObj = {
                    field: field,
                    order: newSortOrder
                  };
                  onFieldChange(changedField);
                }}
                className="option-card-order expanded"
              >
                {sortOrder == SortOrder.desc ? (
                  <>
                    <Descending />
                    <div>desc</div>
                  </>
                ) : (
                  <>
                    <Ascending />
                    <div>asc</div>
                  </>
                )}
              </div>
            </div>
          </>
          <SixDots />
        </div>
      )}
      <Icon onClick={isDisabled ? null : () => onFieldChange(null, index)}>
        <Trash />
      </Icon>
    </div>
  );
};
